import { z } from 'zod';
import { email } from './inputs';
import { isValidTaxNumber, isValidTckn, normalizePersonName } from './identity';

/**
 * Customers: the firms an agency sells to (accounts) and the people who travel
 * (contacts), with their passports, visas and documents. Request schemas here;
 * response shapes in contract.ts.
 *
 * On updates a missing key leaves the field alone; '' or null clears it.
 */

const version = z.number().int().positive();
const id = z.uuid();
const blankToNull = <T>(v: T) => (v === '' ? null : v);

const optional = (max: number) =>
  z.string().trim().max(max).nullable().optional().transform(blankToNull);

const optionalEmail = z
  .union([z.literal(''), email])
  .nullable()
  .optional()
  .transform(blankToNull);

const country = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}$/, 'Ülke iki harfli kodla girilmeli');

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih YYYY-AA-GG biçiminde olmalı')
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Geçerli bir tarih girin');
const optionalDate = z
  .union([z.literal(''), isoDate])
  .nullable()
  .optional()
  .transform(blankToNull);

const personName = z
  .string()
  .trim()
  .min(1, 'Bu alan boş bırakılamaz')
  .max(80)
  .transform(normalizePersonName);

const taxNumber = z
  .string()
  .transform((v) => v.replace(/\s/g, ''))
  .refine(
    (v) => v === '' || /^\d{10,11}$/.test(v),
    'Vergi numarası 10, şahıs şirketi için 11 haneli olmalı',
  )
  .refine(
    (v) => v === '' || isValidTaxNumber(v),
    'Vergi numarası doğrulanamadı; rakamları kontrol edin',
  )
  .nullable()
  .optional()
  .transform(blankToNull);

const nationalId = z
  .string()
  .trim()
  .refine((v) => v === '' || isValidTckn(v), 'TC kimlik numarası geçerli değil')
  .nullable()
  .optional()
  .transform(blankToNull);

// Accounts ---------------------------------------------------------------------

const accountFields = {
  name: z.string().trim().min(1, 'Firma adını girin').max(160),
  legalName: optional(240),
  taxOffice: optional(80),
  taxNumber,
  phone: optional(40),
  email: optionalEmail,
  website: optional(160),
  address: optional(500),
  city: optional(80),
  country: country.optional(),
  notes: optional(4000),
  managerMembershipId: id.nullable().optional(),
};

export const createAccountInput = z.object(accountFields);
export const updateAccountInput = z.object(accountFields).partial().extend({ version });

// Contacts ---------------------------------------------------------------------

const accountLink = z.object({
  accountId: id,
  title: optional(80),
  isPrimary: z.boolean().default(false),
});

const contactFields = {
  firstName: personName,
  lastName: personName,
  gender: z.enum(['female', 'male']).nullable().optional(),
  birthDate: optionalDate,
  nationalId,
  nationality: country.optional(),
  phone: optional(40),
  email: optionalEmail,
  notes: optional(4000),
  accounts: z.array(accountLink).max(20).optional(),
};

export const createContactInput = z.object({
  ...contactFields,
  /** Set after the user has seen the possible duplicates and chosen to go ahead. */
  allowDuplicate: z.boolean().optional(),
});
export const updateContactInput = z.object(contactFields).partial().extend({ version });

// Passports and visas ------------------------------------------------------------

export const PASSPORT_KINDS = ['ordinary', 'special', 'service', 'diplomatic'] as const;
export type PassportKind = (typeof PASSPORT_KINDS)[number];

/** Turkish passports by cover colour, the way agencies talk about them. */
export const PASSPORT_KIND_LABELS: Record<PassportKind, string> = {
  ordinary: 'Umuma mahsus (bordo)',
  special: 'Hususi (yeşil)',
  service: 'Hizmet (gri)',
  diplomatic: 'Diplomatik (siyah)',
};

const passportNumber = z
  .string()
  .transform((v) => v.replace(/\s/g, '').toUpperCase())
  .pipe(z.string().regex(/^[A-Z0-9]{5,20}$/, 'Pasaport numarası 5–20 harf ve rakamdan oluşmalı'));

const passportFields = {
  number: passportNumber,
  country,
  kind: z.enum(PASSPORT_KINDS),
  issueDate: optionalDate,
  expiryDate: isoDate,
  issuingAuthority: optional(120),
  isPrimary: z.boolean().optional(),
  notes: optional(1000),
};

export const passportInput = z.object({
  ...passportFields,
  kind: passportFields.kind.default('ordinary'),
});
export const updatePassportInput = z.object(passportFields).partial().extend({ version });

export const VISA_ENTRIES = ['single', 'double', 'multiple'] as const;
export const VISA_ENTRY_LABELS: Record<(typeof VISA_ENTRIES)[number], string> = {
  single: 'Tek giriş',
  double: 'Çift giriş',
  multiple: 'Çok giriş',
};

const visaFields = {
  /** ISO country code; Schengen visas use "EU". */
  country,
  visaType: optional(60),
  number: optional(40),
  entries: z.enum(VISA_ENTRIES).nullable().optional(),
  durationDays: z.number().int().min(1).max(3650).nullable().optional(),
  issueDate: optionalDate,
  expiryDate: isoDate,
  passportId: id.nullable().optional(),
  notes: optional(1000),
};

export const visaInput = z.object(visaFields);
export const updateVisaInput = z.object(visaFields).partial().extend({ version });

// Documents -----------------------------------------------------------------------

export const DOCUMENT_CATEGORIES = [
  'passport',
  'id_card',
  'visa',
  'photo',
  'ticket',
  'voucher',
  'invoice',
  'other',
] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  passport: 'Pasaport',
  id_card: 'Kimlik kartı',
  visa: 'Vize',
  photo: 'Fotoğraf',
  ticket: 'Bilet',
  voucher: 'Voucher',
  invoice: 'Fatura',
  other: 'Diğer',
};

/** What may be uploaded. The API checks the content (magic bytes), not the name. */
export const DOCUMENT_TYPES = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
  'image/heic': 'HEIC',
} as const;
export type DocumentMimeType = keyof typeof DOCUMENT_TYPES;
export const DOCUMENT_MAX_BYTES = 20 * 1024 * 1024;

/** Days a deleted document stays restorable before the purge job removes it. */
export const DOCUMENT_TRASH_DAYS = 30;

export const updateDocumentInput = z.object({
  title: z.string().trim().min(1, 'Başlık boş bırakılamaz').max(160).optional(),
  category: z.enum(DOCUMENT_CATEGORIES).optional(),
  expiryDate: optionalDate,
});

export const customerListQuery = z.object({
  q: z.string().trim().max(100).optional(),
  archived: z.enum(['true', 'false']).optional(),
  accountId: id.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export type CreateAccountInput = z.input<typeof createAccountInput>;
export type UpdateAccountInput = z.input<typeof updateAccountInput>;
export type CreateContactInput = z.input<typeof createContactInput>;
export type UpdateContactInput = z.input<typeof updateContactInput>;
export type PassportInput = z.input<typeof passportInput>;
export type VisaInput = z.input<typeof visaInput>;

/** Shared with other records that hold the same kinds of field (suppliers). */
export { optionalEmail as optionalEmailInput, taxNumber as taxNumberInput };
